import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import { Box } from '../Box';
import { Heading } from '../Heading';
import { Text } from '../Text';
import { Icon } from '../Icon';
import { ButtonGroup } from '../ButtonGroup';
import { IconButton } from '../IconButton';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';

type StyledBannerProps = {
  $colorScheme: ColorSchemeTypes;
};

const StyledBanner = styled.div<StyledBannerProps>`
  display: flex;
  flex-direction: row;
  background: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-03);
  align-items: center;
  padding: var(--wui-space-04);
  gap: var(--wui-space-02) var(--wui-space-03);
  width: 100%;

  ${({ $colorScheme }) => getColorScheme($colorScheme)};
`;

const StyledIconContainer = styled(Box)`
  max-height: 44px;
`;

const StyledTextContainer = styled(Box)`
  flex: 1 1 340px;
  min-width: 0;
  max-width: fit-content;
  flex-wrap: wrap;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  flex-shrink: 0;
  width: auto;
  max-width: 100%;
  flex-wrap: wrap;
  min-width: 0;
`;

export type BannerProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * The main text content displayed in the banner
   */
  bodyText: ReactNode;
  /**
   * Optional heading text displayed at the top of the banner content
   */
  headingText?: string;
  /**
   * Optional `Icon` component to use. Must be an instance of [Icon](../?path=/docs/components-icon--docs)
   */
  icon?: JSX.Element;
  colorScheme?: ColorSchemeTypes;
  /**
   * Primary call-to-action element (typically a [Button]() of the `solid` variant)
   */
  primaryAction?: ReactNode;
  /**
   * Secondary call-to-action element (typically a [Button]() of the `outline` variant)
   */
  secondaryAction?: ReactNode;
  /**
   * Optional callback function when the close button is clicked. If not provided, the close button will not be displayed.
   */
  onClose?: () => void;
};

/**
 * The `Banner` component is for displaying contextual information to the user such as alerts or feature announcements.
 */
export const Banner = ({
  bodyText,
  colorScheme = 'inherit',
  headingText,
  icon,
  onClose,
  primaryAction,
  secondaryAction,
  ...props
}: BannerProps): JSX.Element => {
  const hasActions = isNotNil(primaryAction) || isNotNil(secondaryAction);

  return (
    <StyledBanner
      {...props}
      $colorScheme={colorScheme}
    >
      {isNotNil(icon) && (
        <Box
          alignSelf="stretch"
          direction="column"
          justifyContent="flex-start"
          style={{ paddingTop: 'var(--wui-space-01)' }}
        >
          {icon}
        </Box>
      )}
      <Box
        alignItems="stretch"
        direction="row"
        flexMode="grow"
        gap={{ column: 'space-04', row: 'space-03' }}
        justifyContent="space-between"
        wrapItems={true}
      >
        <StyledTextContainer
          direction="column"
          gap="space-00"
          justifyContent="center"
        >
          {isNotNil(headingText) ? <Heading variant="heading5">{headingText}</Heading> : null}

          <Text variant="body3">{bodyText}</Text>
        </StyledTextContainer>

        {hasActions ? (
          <StyledButtonGroup
            buttonSize="md"
            collapseOnSmallScreens={false}
          >
            {isNotNil(primaryAction) && primaryAction}
            {isNotNil(secondaryAction) && secondaryAction}
          </StyledButtonGroup>
        ) : null}
      </Box>

      {isNotNil(onClose) && (
        <StyledIconContainer
          alignSelf="stretch"
          direction="column"
          justifyContent="center"
        >
          <IconButton
            label="Close"
            onClick={onClose}
            size="md"
            variant="soft"
          >
            <Icon type="close" />
          </IconButton>
        </StyledIconContainer>
      )}
    </StyledBanner>
  );
};

Banner.displayName = 'Banner';
